import type { ReactNode } from "react"
import { Link } from "react-router"
import { cn } from "@/primitives/utils"

/*
 * A link drawn as text, in a sentence or standing alone in a row: "Review it", "The thread you
 * asked in". It reads in the brand's text colour, underlined on hover, and on touch reaches a 44px
 * hit area past its line (`touch-target`), so a finger finds it however short its words.
 */

/**
 * `to` is a page of the web app, with the `state` its page reads (where it was opened from); `href`
 * is somewhere else, such as a Slack thread, which opens in a new tab and is told nothing of where
 * it came from. `emphasis` sets it in the medium weight, for the one link a callout offers.
 * It takes its type size from the text it stands in. Nothing clips it, so its hit area reaches.
 */
export function TextLink({
  emphasis = false,
  children,
  ...target
}: ({ to: string; state?: unknown } | { href: string }) & {
  emphasis?: boolean
  children: ReactNode
}) {
  const className = cn(
    "text-brand-text underline-offset-4 touch-target hover:underline",
    emphasis && "font-medium",
  )
  if ("href" in target)
    return (
      <a className={className} href={target.href} target="_blank" rel="noreferrer noopener">
        {children}
      </a>
    )
  return (
    <Link className={className} to={target.to} state={target.state}>
      {children}
    </Link>
  )
}
